import {assert} from 'Asserts';
import * as BenchlingUtil from 'BenchlingUtil';
import {
  ProcessExecutionType,
  ProcessPlatformFeatureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import makeSimpleActionFunction from 'notebook/structured-tables/action-functions/makeSimpleActionFunction';
import {
  StructuredTableKind,
  StructuredTableSchema,
} from 'notebook/structured-tables/StructuredTableConstants';
import {
  getOutputsFilteredByProcessesUrl,
  getOutputsFilteredByTasksUrl,
  getTasksFilteredByProcessesUrl,
} from 'processes/dashboard/getModelFilteredByProcessesrUrl';
import {ProcessTableLocation} from 'processes/ProcessConstants';
import {getProcessPlatformFeatureConfig} from 'processes/processes-platform/FeatureOptions';
import {
  getOutputSchema,
  ProcessOutputStructuredTable,
  ProcessOutputStructuredTableMetadata,
} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import {getTaskSchema, ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';

function getOutputsDashboardUrl(
  metadata: ProcessOutputStructuredTableMetadata,
  schema: StructuredTableSchema
): string {
  // If both a taskId and processId are provided, filter by task since it's more restrictive.
  if (metadata.taskId != null) {
    return getOutputsFilteredByTasksUrl([metadata.taskId]);
  }
  if (metadata.processId != null) {
    return getOutputsFilteredByProcessesUrl([metadata.processId], schema?.id);
  }
  assert(false, 'Expected output structured table metadata to contain a processId or taskId');
}

export function openInDashboard(
  structuredTable: ProcessTaskStructuredTable | ProcessOutputStructuredTable
): void {
  let dashboardUrl: string | null;
  const schema = structuredTable.schemas[0];
  switch (structuredTable.kind) {
    case StructuredTableKind.PROCESS_TASK:
      if (structuredTable.metadata.processId == null) {
        assert(false, 'Expected task structured table metadata to contain a processId');
        return;
      }
      dashboardUrl = getTasksFilteredByProcessesUrl([structuredTable.metadata.processId], schema?.id);
      break;
    case StructuredTableKind.PROCESS_OUTPUT:
      dashboardUrl = getOutputsDashboardUrl(
        structuredTable.metadata as ProcessOutputStructuredTableMetadata,
        schema
      );
      break;
    default:
      assert(
        false,
        `Expected structured table to be one of ${StructuredTableKind.PROCESS_TASK} or ${StructuredTableKind.PROCESS_OUTPUT}`
      );
      return;
  }
  BenchlingUtil.navigate(null, dashboardUrl);
}

export function getHidden({
  structuredTable,
}: {
  structuredTable: ProcessTaskStructuredTable | ProcessOutputStructuredTable;
}): boolean {
  let isLocationValid: boolean;
  let isMetadataContextValid: boolean;
  let executionType: ProcessExecutionType | null = null;
  let taskSchemaName: string | null = null;

  switch (structuredTable.kind) {
    case StructuredTableKind.PROCESS_TASK: {
      isLocationValid = structuredTable.metadata.tableLocation === ProcessTableLocation.WORKSPACE_TAB;
      isMetadataContextValid = structuredTable.metadata.processId != null;
      const taskSchema = getTaskSchema(structuredTable.schemas);
      executionType = taskSchema?.executionType ?? null;
      taskSchemaName = taskSchema?.name ?? null;
      break;
    }
    case StructuredTableKind.PROCESS_OUTPUT: {
      isLocationValid =
        structuredTable.metadata.tableLocation === ProcessTableLocation.WORKSPACE_TAB ||
        structuredTable.metadata.tableLocation === ProcessTableLocation.TASK_DETAILS_OUTPUTS_TAB;
      isMetadataContextValid =
        structuredTable.metadata.processId != null ||
        (structuredTable.metadata as ProcessOutputStructuredTableMetadata).taskId != null;
      const outputSchema = getOutputSchema(structuredTable.schemas);
      executionType = outputSchema?.processExecutionType ?? null;
      taskSchemaName = outputSchema?.processTaskSchemaName ?? null;
      break;
    }
    default:
      assert(
        false,
        `Expected structured table to be one of ${StructuredTableKind.PROCESS_TASK} or ${StructuredTableKind.PROCESS_OUTPUT}`
      );
      return true;
  }

  if (!isLocationValid || !isMetadataContextValid) {
    return true;
  }
  return (
    executionType == null ||
    taskSchemaName == null ||
    !getProcessPlatformFeatureConfig(
      ProcessPlatformFeatureType.WORKFLOWS_SEARCH,
      executionType,
      taskSchemaName
    )
  );
}

const openInDashboardActionFunction = makeSimpleActionFunction({
  label: 'Open in dashboard',
  icon: 'open',
  getHidden,
  onInvoke: ({structuredTable}) => {
    openInDashboard(structuredTable);
  },
  getDisabled: ({
    structuredTable,
  }: {
    structuredTable: ProcessTaskStructuredTable | ProcessOutputStructuredTable;
  }) => !structuredTable.rows.some((row) => row.isFinalized),
});

export default openInDashboardActionFunction;
